import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import FullCalendar from '@fullcalendar/react';
import timeGridPlugin from '@fullcalendar/timegrid';
import { Box, Button, GlobalStyles, Stack, Typography, Menu, MenuItem } from '@mui/material';
import { addDays, format, startOfMonth, endOfMonth } from 'date-fns';
import { useState, useMemo, useCallback, useRef } from 'react';

import {
  CalendarEvent,
  EventCategory,
  ReminderType,
  RecurringPattern,
  RecurringPeriod,
  ReminderUnit,
} from '@/types/calendar';

import DashboardHeader from '@/@mui/dashboard/DashboardHeader';
import MuiPageWrapper from '@/@mui/MuiPageWrapper';
import { AICalendarEvent } from '@/api/endpoints/calendar/useGetAICalendarEvents';
import { useGetAllCalendarEvents } from '@/api/endpoints/calendar/useGetAllCalendarEvents';
import { Id } from '@/api/utils/sql';
import { PlusIcon } from '@/assets/icons/PlusIcon';
import { usePropertyManager } from '@/context/PropertyManagerProvider';

import CalendarEventCreateModal from './CalendarEventCreateModal';
import CalendarEventEditModal from './CalendarEventEditModal';
import EventDetailsModal from './EventDetailsModal';

// Define a common interface for display events
interface DisplayEvent {
  id: string | number;
  title: string;
  start: string;
  end: string;
  all_day: boolean;
  description?: string | null;
  isAiEvent?: boolean;
  // Optional CalendarEvent properties
  propertyLocationId?: Id;
  buildingId?: Id;
  unitId?: Id;
  tenantId?: Id | string;
  created_by?: string;
  created_at?: string;
  updated_at?: string;
  location?: {
    id: Id;
    name: string;
    type: string;
  };
  building?: {
    id: Id;
    name: string;
  };
  unit?: {
    id: Id;
    name: string;
  };
  tenant?: {
    id: Id;
    full_name: string;
  };
  external_document_id?: string;
  document_metadata?: {
    metadata: {
      data: {
        document: {
          summary: string;
        };
      };
    };
  };
  // Additional fields for enhanced event details
  category?: EventCategory;
  reminderType?: ReminderType;
  recurringPattern?: RecurringPattern;
  recurringEvery?: number;
  recurringPeriod?: RecurringPeriod;
  neverExpires?: boolean;
  reminderIntervals?: Array<{ value: number; unit: ReminderUnit }>;
  notifySelf?: boolean;
  notifyTenant?: boolean;
  notifyVendor?: boolean;
  emailNotification?: boolean;
  smsNotification?: boolean;
  pushNotification?: boolean;
  dashboardNotification?: boolean;
  vendorId?: string;
  vendorEmail?: string;
  vendorMessage?: string;
}

// Event color mappings exactly matching the Figma design
const EVENT_COLORS = {
  default: {
    bg: '#FAFAFA',
    border: '#E9EAEB',
    text: '#414651',
    timeText: '#535862',
  },
  blue: {
    bg: '#EFF8FF',
    border: '#B2DDFF',
    text: '#175CD3',
    timeText: '#1570EF',
  },
  purple: {
    bg: '#F9F5FF',
    border: '#E9D7FE',
    text: '#6941C6',
    timeText: '#7F56D9',
  },
  indigo: {
    bg: '#EEF4FF',
    border: '#C7D7FE',
    text: '#3538CD',
    timeText: '#444CE7',
  },
  green: {
    bg: '#EDFCF2',
    border: '#AAF0C4',
    text: '#087443',
    timeText: '#099250',
  },
  orange: {
    bg: '#FEF6EE',
    border: '#F9DBAF',
    text: '#B93815',
    timeText: '#E04F16',
  },
  pink: {
    bg: '#FDF2FA',
    border: '#FCCEEE',
    text: '#C11574',
    timeText: '#DD2590',
  },
  yellow: {
    bg: '#FEFBE8',
    border: '#FEEE95',
    text: '#A15C07',
    timeText: '#CA8504',
  },
  lightPurple: {
    bg: '#F4F3FF',
    border: '#D9D6FE',
    text: '#5925DC',
    timeText: '#6938EF',
  },
};

// Define component props for CalendarEventContent
interface CalendarEventContentProps {
  event: {
    title: string;
    extendedProps: Record<string, unknown>;
  };
}

// Define component props for MoreLinkContent
interface MoreLinkContentProps {
  num: number;
}

type CalendarViewType = 'dayGridMonth' | 'timeGridWeek' | 'timeGridDay';

// Define the color type based on EVENT_COLORS structure
type EventColor = {
  text: string;
  timeText: string;
  bg: string;
  border: string;
};

// Calendar event content component - moved outside main component
const CalendarEventContent = ({ event }: CalendarEventContentProps) => {
  const color = event.extendedProps.color as EventColor;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
      <Box
        sx={{
          fontWeight: 600,
          fontSize: '12px',
          color: color.text,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {event.title}
      </Box>
    </Box>
  );
};

// More link content component - moved outside main component
const MoreLinkContent = ({ num }: MoreLinkContentProps) => (
  <Box sx={{ fontSize: '12px', color: '#717680', fontWeight: 600 }}>{num} more...</Box>
);

const CalendarView = () => {
  const calendarRef = useRef<FullCalendar | null>(null);
  const [isAddEventOpen, setIsAddEventOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null);
  const [viewingEvent, setViewingEvent] = useState<DisplayEvent | null>(null);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const { selectedPropertyLocation } = usePropertyManager();
  const [dateRange, setDateRange] = useState({
    start: format(startOfMonth(new Date()), 'yyyy-MM-dd'),
    end: format(endOfMonth(new Date()), 'yyyy-MM-dd'),
  });
  const [calendarView, setCalendarView] = useState<CalendarViewType>('dayGridMonth');
  const [viewMenuAnchor, setViewMenuAnchor] = useState<null | HTMLElement>(null);

  // View menu state
  const isViewMenuOpen = Boolean(viewMenuAnchor);
  const handleViewMenuClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setViewMenuAnchor(event.currentTarget);
  };
  const handleViewMenuClose = () => {
    setViewMenuAnchor(null);
  };

  // View options memoized to prevent unnecessary re-renders
  const viewOptions = useMemo(
    () => [
      { value: 'dayGridMonth', label: 'Month view' },
      { value: 'timeGridWeek', label: 'Week view' },
      { value: 'timeGridDay', label: 'Day view' },
    ],
    []
  );

  // Get current view display label
  const currentViewLabel = useMemo(
    () => viewOptions.find(option => option.value === calendarView)?.label || 'Month view',
    [calendarView, viewOptions]
  );

  const { data: calendarEvents = { events: [], aiEvents: [] }, refetch } = useGetAllCalendarEvents(
    {
      property_location_id: selectedPropertyLocation?.id ?? 0,
      start_date: dateRange.start,
      end_date: dateRange.end,
    },
    !!selectedPropertyLocation?.id
  );

  // Generate color for each event based on ID, matching Figma
  const getEventColor = (id: string | number) => {
    const colorKeys = Object.keys(EVENT_COLORS);
    const hash = String(id)
      .split('')
      .reduce((acc, char) => acc + char.charCodeAt(0), 0);
    const colorIndex = hash % colorKeys.length;
    return EVENT_COLORS[colorKeys[colorIndex] as keyof typeof EVENT_COLORS];
  };

  // Calendar navigation handlers
  const handlePrevClick = () => {
    if (calendarRef.current) {
      calendarRef.current.getApi().prev();
    }
  };

  const handleNextClick = () => {
    if (calendarRef.current) {
      calendarRef.current.getApi().next();
    }
  };

  const handleTodayClick = () => {
    if (calendarRef.current) {
      calendarRef.current.getApi().today();
    }
  };

  const handleViewChange = (newView: CalendarViewType) => {
    setCalendarView(newView);
    handleViewMenuClose();
    // Use the calendar API to change the view
    if (calendarRef.current) {
      calendarRef.current.getApi().changeView(newView);
    }
  };

  // Format events for FullCalendar
  const formattedEvents = useMemo(() => {
    // Process regular calendar events
    const eventsData = calendarEvents.events || [];

    const formattedRegularEvents: DisplayEvent[] = eventsData.map((event: CalendarEvent) => {
      // Create a base formatted event from the calendar event
      const formattedEvent: DisplayEvent = {
        ...event,
        // Map standard metadata fields
        category: event.metadata?.category,
        notifySelf: event.metadata?.notifySelf,
        notifyTenant: event.metadata?.notifyTenant,
        notifyVendor: event.metadata?.notifyVendor,
        emailNotification: event.metadata?.emailNotification,
        smsNotification: event.metadata?.smsNotification,
        pushNotification: event.metadata?.pushNotification,
        dashboardNotification: event.metadata?.dashboardNotification,
        vendorId: event.metadata?.vendor?.id,
        vendorEmail: event.metadata?.vendor?.email,
        vendorMessage: event.metadata?.vendor?.message,
      };

      // Map recurring settings if available
      if (event.recurringSettings) {
        formattedEvent.reminderType = ReminderType.RECURRING;
        formattedEvent.recurringPattern = event.recurringSettings.pattern;
        formattedEvent.recurringEvery = event.recurringSettings.every;
        formattedEvent.recurringPeriod = event.recurringSettings.period;
        formattedEvent.neverExpires = !event.recurringSettings.end_date;
      } else {
        formattedEvent.reminderType = ReminderType.ONE_TIME;
      }

      // Map reminders if available
      if (event.reminders && event.reminders.length > 0) {
        formattedEvent.reminderIntervals = event.reminders.map(reminder => ({
          value: reminder.value,
          unit: reminder.unit,
        }));
      }

      // Handle all-day events by adjusting the date format
      if (event.all_day) {
        const endDate = new Date(event.end);
        const nextDay = addDays(endDate, 1);

        const [startDateOnly] = new Date(event.start).toISOString().split('T');
        const [endDateOnly] = nextDay.toISOString().split('T');

        formattedEvent.start = startDateOnly;
        formattedEvent.end = endDateOnly;
      }

      return formattedEvent;
    });

    // Process AI calendar events from backend if available
    const backendAiEvents: DisplayEvent[] =
      calendarEvents.aiEvents && Array.isArray(calendarEvents.aiEvents)
        ? calendarEvents.aiEvents.map((aiEvent: AICalendarEvent | CalendarEvent, index) => {
          // Determine if this is an AI event or regular event based on properties
          const isAiEvent = 'event_date' in aiEvent && 'event_type' in aiEvent;

          // Type guard for external_document_id to ensure it's string or undefined
          const getExternalDocumentId = (): string | undefined => {
            if (isAiEvent && typeof aiEvent.external_document_id === 'string') {
              return aiEvent.external_document_id;
            }
            if (
              !isAiEvent &&
                'external_document_id' in aiEvent &&
                typeof aiEvent.external_document_id === 'string'
            ) {
              return aiEvent.external_document_id;
            }
            return undefined;
          };

          return {
            id: `backend-ai-${index}`,
            title: aiEvent.description || '',
            // Use event_date if it exists, otherwise use start
            start: isAiEvent ? aiEvent.event_date : aiEvent.start,
            end: isAiEvent ? aiEvent.event_date : aiEvent.end,
            description: aiEvent.description || '',
            // Use the type guard function to get correctly typed external_document_id
            external_document_id: getExternalDocumentId(),
            // Handle tenant_id/tenantId naming differences
            tenantId: isAiEvent ? aiEvent.tenant_id : (aiEvent as CalendarEvent).tenantId,
            isAiEvent: true,
            all_day: true,
          };
        })
        : [];

    return [...formattedRegularEvents, ...backendAiEvents];
  }, [calendarEvents]);

  const handleEventClick = (info: { event: { id: string } }) => {
    const event = formattedEvents.find(e => e.id.toString() === info.event.id);
    if (event) {
      // Show details for all event types
      setViewingEvent(event);
    }
  };

  const handleEditEvent = (eventToEdit: DisplayEvent) => {
    // Only set selectedEvent for regular calendar events, not AI events
    // The check for !isAiEvent is done in EventDetailsModal before showing the button.
    // All non-AI events should be derived from CalendarEvent and have the necessary IDs.
    if (eventToEdit && !eventToEdit.isAiEvent) {
      setSelectedEvent(eventToEdit as CalendarEvent);
      setViewingEvent(null); // Explicitly close the details modal
    } else {
      // This case should ideally not be hit.
      // You might want to log here if debugging:
      // console.log("Edit event called on an AI event or unexpected event:", eventToEdit);
    }
  };

  const handleEventCreated = () => {
    refetch();
  };

  const handleEventDeleted = () => {
    refetch();
  };

  const handleEventUpdated = () => {
    refetch().then(({ data }) => {
      // If we're currently viewing an event, update it with fresh data
      if (viewingEvent) {
        const updatedEvent = data?.events?.find(e => e.id === viewingEvent.id);
        if (updatedEvent) {
          // Format the updated event to match the DisplayEvent interface
          const formattedEvent: DisplayEvent = {
            ...updatedEvent,
            // Map standard metadata fields
            category: updatedEvent.metadata?.category,
            notifySelf: updatedEvent.metadata?.notifySelf,
            notifyTenant: updatedEvent.metadata?.notifyTenant,
            notifyVendor: updatedEvent.metadata?.notifyVendor,
            emailNotification: updatedEvent.metadata?.emailNotification,
            smsNotification: updatedEvent.metadata?.smsNotification,
            pushNotification: updatedEvent.metadata?.pushNotification,
            dashboardNotification: updatedEvent.metadata?.dashboardNotification,
            vendorId: updatedEvent.metadata?.vendor?.id,
            vendorEmail: updatedEvent.metadata?.vendor?.email,
            vendorMessage: updatedEvent.metadata?.vendor?.message,
          };

          // Map recurring settings if available
          if (updatedEvent.recurringSettings) {
            formattedEvent.reminderType = ReminderType.RECURRING;
            formattedEvent.recurringPattern = updatedEvent.recurringSettings.pattern;
            formattedEvent.recurringEvery = updatedEvent.recurringSettings.every;
            formattedEvent.recurringPeriod = updatedEvent.recurringSettings.period;
            formattedEvent.neverExpires = !updatedEvent.recurringSettings.end_date;
          } else {
            formattedEvent.reminderType = ReminderType.ONE_TIME;
          }

          // Update the viewing event with the latest data
          setViewingEvent(formattedEvent);
        }
      }
    });
  };

  const handleDatesSet = useCallback(
    (info: { start: Date; end: Date }) => {
      const newStartDate = format(info.start, 'yyyy-MM-dd');
      const newEndDate = format(info.end, 'yyyy-MM-dd');

      if (newStartDate !== dateRange.start || newEndDate !== dateRange.end) {
        setDateRange({
          start: newStartDate,
          end: newEndDate,
        });
        // Refetch events with new date range
        refetch();
      }
    },
    [dateRange, refetch]
  );

  // Create calendar styles object for GlobalStyles - updated to match Figma exactly
  const calendarStyles = useMemo(
    () => ({
      '.fc': {
        fontFamily: 'Inter, -apple-system, sans-serif',
        '--fc-border-color': '#E9EAEB',
        '--fc-button-text-color': '#414651',
        '--fc-button-bg-color': '#FFFFFF',
        '--fc-button-border-color': '#D5D7DA',
        '--fc-button-hover-bg-color': '#F8F9FA',
        '--fc-button-hover-border-color': '#D5D7DA',
        '--fc-button-active-bg-color': '#F8F9FA',
        '--fc-today-bg-color': '#FFFFFF',
        color: '#414651',
      },
      '.fc-header-toolbar': {
        marginBottom: '20px',
        '& .fc-button': {
          borderRadius: '8px',
          boxShadow:
            '0px 1px 2px 0px rgba(16, 24, 40, 0.05), inset 0px -2px 0px 0px rgba(10, 13, 18, 0.05), inset 0px 0px 0px 1px rgba(10, 13, 18, 0.18)',
          fontWeight: 600,
          fontSize: '14px',
          color: '#414651',
          backgroundColor: '#FFFFFF',
          borderColor: '#D5D7DA',
          '&:focus': {
            boxShadow: '0px 1px 2px 0px rgba(16, 24, 40, 0.05)',
          },
          '&:hover': {
            backgroundColor: '#F8F9FA',
            borderColor: '#D5D7DA',
          },
          '&:active, &.fc-button-active': {
            backgroundColor: '#F8F9FA',
            borderColor: '#D5D7DA',
          },
        },
        '& .fc-today-button': {
          padding: '8px 16px',
        },
        '& .fc-prev-button, & .fc-next-button': {
          padding: '8px 12px',
        },
        // Hide the default title since we're providing our own
        '& .fc-toolbar-title': {
          display: 'none',
        },
      },
      '.fc-view-harness': {
        borderRadius: '12px',
        border: '1px solid #E9EAEB',
        overflow: 'hidden',
        boxShadow: '0px 1px 2px 0px rgba(10, 13, 18, 0.05)',
      },
      '.fc-scrollgrid': {
        borderRadius: '12px',
        border: 'none !important',
        overflow: 'hidden',
      },
      '.fc-scrollgrid-section-header td': {
        border: 'none',
      },
      '.fc-scrollgrid-section-body td': {
        border: '1px solid #E9EAEB',
      },
      '.fc-col-header': {
        marginTop: 0,
      },
      '.fc-col-header-cell': {
        padding: '8px 0',
        backgroundColor: '#FFFFFF',
        borderBottom: '1px solid #E9EAEB',
        '& .fc-col-header-cell-cushion': {
          padding: '8px',
          color: '#535862',
          fontWeight: 500,
          fontSize: '12px',
          textDecoration: 'none',
        },
      },
      '.fc-h-event': {
        backgroundColor: '#FAFAFA',
        border: '1px solid #E9EAEB',
        borderRadius: '6px',
        color: '#414651',
        cursor: 'pointer',
        padding: '4px 8px',
        boxShadow: '0px 1px 2px 0px rgba(10, 13, 18, 0.05)',
        margin: '2px 0',
      },
      '.fc-day-today': {
        backgroundColor: '#FFFFFF !important',
      },
      '.fc-day-today .fc-daygrid-day-top .fc-daygrid-day-number': {
        backgroundColor: '#9D1500',
        color: '#FFFFFF',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        width: '24px',
        height: '24px',
        borderRadius: '50%',
      },
      '.fc-daygrid-day:hover': {
        backgroundColor: '#F8F9FA',
      },
      '.fc-day-other': {
        backgroundColor: '#FDFDFD',
      },
      '.fc-daygrid-day-frame': {
        padding: '8px',
      },
      '.fc-scrollgrid-sync-inner': {
        minHeight: '100px',
      },
      '.fc-daygrid-day-top': {
        justifyContent: 'flex-start',
      },
      '.fc-daygrid-day-number': {
        width: 'auto',
        padding: '8px',
        textAlign: 'left',
        fontSize: '12px',
        fontWeight: 600,
        color: '#414651',
        textDecoration: 'none',
      },
      '.fc-day-other .fc-daygrid-day-number': {
        color: '#535862',
        opacity: 0.7,
      },
      '.fc-daygrid-day-events': {
        marginTop: 4,
      },
      '.fc-daygrid-more-link': {
        color: '#717680',
        fontSize: '12px',
        fontWeight: 600,
        padding: '0 8px',
        textDecoration: 'none',
      },
      '.fc-popover': {
        zIndex: '1 !important',
        borderRadius: '8px',
        boxShadow: '0px 4px 8px rgba(10, 13, 18, 0.1)',
        border: '1px solid #E9EAEB',
      },
      '.fc-popover-header': {
        backgroundColor: '#FAFAFA',
        padding: '8px 12px',
        borderBottom: '1px solid #E9EAEB',
      },
      '.fc-popover-body': {
        padding: '8px',
      },
      '.fc-event-title': {
        padding: '0',
        fontSize: '12px',
        fontWeight: 600,
        color: '#414651',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      },
      '.fc-event-time': {
        fontSize: '12px',
        color: '#535862',
      },
      '.fc-daygrid-event-dot': {
        display: 'none',
      },
      // Custom date badge styling
      '.custom-date-badge': {
        display: 'flex',
        flexDirection: 'column',
        width: '64px',
        height: '64px',
        borderRadius: '8px',
        overflow: 'hidden',
        boxShadow: '0px 1px 2px 0px rgba(10, 13, 18, 0.05)',
        border: '1px solid #E9EAEB',
        backgroundColor: '#FFFFFF',
        marginRight: '12px',
      },
      '.date-badge-month': {
        backgroundColor: '#FAFAFA',
        color: '#717680',
        fontSize: '12px',
        fontWeight: 600,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '4px 8px 2px',
      },
      '.date-badge-day': {
        backgroundColor: '#FFFFFF',
        color: '#9D1500',
        fontSize: '18px',
        fontWeight: 700,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        flex: 1,
        padding: '1px 8px 3px',
      },
      // Week badge styling
      '.week-badge': {
        backgroundColor: '#FAFAFA',
        border: '1px solid #D5D7DA',
        borderRadius: '6px',
        padding: '2px 6px',
        fontSize: '12px',
        fontWeight: 500,
        color: '#414651',
        marginLeft: '8px',
        boxShadow: '0px 1px 2px 0px rgba(10, 13, 18, 0.05)',
        display: 'inline-flex',
        alignItems: 'center',
      },
      // Add event button styling
      '.add-event-btn': {
        backgroundColor: '#9D1500',
        borderRadius: '8px',
        boxShadow:
          '0px 1px 2px 0px rgba(16, 24, 40, 0.05), inset 0px -2px 0px 0px rgba(10, 13, 18, 0.05)',
        border: '2px solid transparent',
        '&:hover': {
          backgroundColor: '#8A1200',
        },
      },
      // Search field styling
      '.calendar-search': {
        position: 'relative',
        transition: 'all 0.3s',
        '& input': {
          border: 'none',
          background: 'transparent',
          outline: 'none',
          width: 0,
          padding: 0,
          transition: 'width 0.3s',
          position: 'absolute',
          right: '40px',
          top: '9px',
        },
        '&.active': {
          '& input': {
            width: '150px',
            padding: '0 10px',
          },
        },
      },
    }),
    []
  );

  // Format the current month and year for display
  const currentMonthYear = useMemo(() => {
    const now = new Date();
    return format(now, 'MMMM yyyy');
  }, []);

  // Get current week number
  const currentWeek = useMemo(() => {
    const now = new Date();
    const startOfYear = new Date(now.getFullYear(), 0, 1);
    const days = Math.floor((now.getTime() - startOfYear.getTime()) / (24 * 60 * 60 * 1000));
    return Math.ceil((days + startOfYear.getDay() + 1) / 7);
  }, []);

  // Get current day and month for the badge
  const currentDate = useMemo(() => {
    const now = new Date();
    return {
      day: format(now, 'd'),
      month: format(now, 'MMM').toUpperCase(),
    };
  }, []);

  // Format date range
  const dateRangeFormatted = useMemo(() => {
    const start = new Date(dateRange.start);
    const end = new Date(dateRange.end);
    return `${format(start, 'MMM d, yyyy')} – ${format(end, 'MMM d, yyyy')}`;
  }, [dateRange]);

  return (
    <MuiPageWrapper>
      <GlobalStyles styles={calendarStyles} />
      <Box sx={{ height: 'calc(100vh - 200px)' }}>
        <DashboardHeader hasInnerHtml title="Calendar: <em>All</em>" />
        <Box
          sx={{
            mb: 3,
            borderRadius: '12px',
            overflow: 'hidden',
            boxShadow: '0px 1px 2px 0px rgba(10, 13, 18, 0.05)',
            border: '1px solid #E9EAEB',
          }}
        >
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="flex-start"
            sx={{
              p: '20px 24px',
              borderBottom: '1px solid #E9EAEB',
              backgroundColor: '#FFFFFF',
            }}
          >
            <Stack direction="row" alignItems="flex-start">
              <Box className="custom-date-badge">
                <Box className="date-badge-month">{currentDate.month}</Box>
                <Box className="date-badge-day">{currentDate.day}</Box>
              </Box>
              <Stack spacing={0.5}>
                <Stack direction="row" alignItems="center">
                  <Typography sx={{ fontSize: '18px', fontWeight: 600, color: '#181D27' }}>
                    {currentMonthYear}
                  </Typography>
                  <Box className="week-badge">Week {currentWeek}</Box>
                </Stack>
                <Typography sx={{ color: '#535862', fontSize: '14px', fontWeight: 400 }}>
                  {dateRangeFormatted}
                </Typography>
              </Stack>
            </Stack>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Stack
                direction="row"
                sx={{
                  overflow: 'hidden',
                  borderRadius: '24px',
                  boxShadow:
                    '0px 1px 2px 0px rgba(16, 24, 40, 0.05), inset 0px -2px 0px 0px rgba(10, 13, 18, 0.05), inset 0px 0px 0px 1px rgba(10, 13, 18, 0.18)',
                  border: '1px solid #D5D7DA',
                }}
              >
                <Button
                  sx={{
                    p: '8px 12px',
                    minWidth: 'auto',
                    borderRadius: '0',
                    borderRight: '1px solid #D5D7DA',
                    color: '#717680',
                    bgcolor: '#FFFFFF',
                  }}
                  onClick={handlePrevClick}
                >
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M12.5 15L7.5 10L12.5 5"
                      stroke="#717680"
                      strokeWidth="1.66667"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </Button>
                <Button
                  sx={{
                    p: '8px 16px',
                    color: '#414651',
                    bgcolor: '#FFFFFF',
                    fontWeight: 600,
                    fontSize: '14px',
                    borderRadius: '0',
                  }}
                  onClick={handleTodayClick}
                >
                  Today
                </Button>
                <Button
                  sx={{
                    p: '8px 12px',
                    minWidth: 'auto',
                    borderRadius: '0',
                    borderLeft: '1px solid #D5D7DA',
                    color: '#717680',
                    bgcolor: '#FFFFFF',
                  }}
                  onClick={handleNextClick}
                >
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M7.5 5L12.5 10L7.5 15"
                      stroke="#717680"
                      strokeWidth="1.66667"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </Button>
              </Stack>
              <Button
                variant="outlined"
                endIcon={
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M5 7.5L10 12.5L15 7.5"
                      stroke="#414651"
                      strokeWidth="1.66667"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                }
                sx={{
                  fontWeight: 600,
                  fontSize: '14px',
                  p: '10px 14px',
                  borderRadius: '8px',
                  border: '1px solid #D5D7DA',
                  color: '#414651',
                  boxShadow: '0px 1px 2px 0px rgba(16, 24, 40, 0.05)',
                }}
                onClick={handleViewMenuClick}
                aria-controls={isViewMenuOpen ? 'view-menu' : undefined}
                aria-haspopup="true"
                aria-expanded={isViewMenuOpen ? 'true' : undefined}
              >
                {currentViewLabel}
              </Button>
              <Menu
                id="view-menu"
                anchorEl={viewMenuAnchor}
                open={isViewMenuOpen}
                onClose={handleViewMenuClose}
                MenuListProps={{
                  'aria-labelledby': 'view-selector-button',
                }}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
              >
                {viewOptions.map(option => (
                  <MenuItem
                    key={option.value}
                    onClick={() => handleViewChange(option.value as CalendarViewType)}
                    selected={calendarView === option.value}
                    sx={{
                      fontSize: '14px',
                      fontWeight: calendarView === option.value ? 600 : 400,
                    }}
                  >
                    {option.label}
                  </MenuItem>
                ))}
              </Menu>
              <Button
                variant="contained"
                startIcon={<PlusIcon />}
                className="add-event-btn"
                onClick={() => setIsAddEventOpen(true)}
                sx={{
                  px: 2.5,
                  py: 1.25,
                  fontWeight: 600,
                  fontSize: '14px',
                }}
              >
                Add event
              </Button>
            </Stack>
          </Stack>
          <Box>
            <FullCalendar
              ref={calendarRef}
              plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
              initialView={calendarView}
              headerToolbar={{
                left: '',
                center: '',
                right: '',
              }}
              dayHeaderFormat={{ weekday: 'short' }}
              contentHeight="auto"
              events={formattedEvents.map(event => {
                const color = getEventColor(event.id);
                return {
                  id: event.id.toString(),
                  title: event.title,
                  description: event.description,
                  start: event.start,
                  end: event.end,
                  allDay: event.all_day,
                  extendedProps: {
                    time: new Date(event.start).toLocaleTimeString([], {
                      hour: 'numeric',
                      minute: '2-digit',
                      hour12: true,
                    }),
                    color,
                    // Add original event to extendedProps for access in click handler
                    originalEvent: event,
                  },
                  backgroundColor: color.bg,
                  borderColor: color.border,
                  textColor: color.text,
                  display: 'block',
                };
              })}
              eventContent={CalendarEventContent}
              eventClick={info => {
                // Access original event data from extendedProps
                const originalEvent = info.event.extendedProps.originalEvent as DisplayEvent;
                if (originalEvent) {
                  handleEventClick({ event: { id: info.event.id } });
                }
              }}
              dateClick={info => {
                setSelectedDate(info.date);
                setIsAddEventOpen(true);
              }}
              height="auto"
              displayEventTime={false}
              eventTimeFormat={{
                hour: 'numeric',
                minute: '2-digit',
                meridiem: true,
              }}
              datesSet={handleDatesSet}
              dayMaxEvents={3}
              moreLinkContent={MoreLinkContent}
              firstDay={1} // Start week on Monday
              fixedWeekCount={false}
              showNonCurrentDates
              timeZone="UTC"
              forceEventDuration
              defaultAllDay
            />
          </Box>
        </Box>

        {isAddEventOpen && (
          <CalendarEventCreateModal
            onClose={() => {
              setIsAddEventOpen(false);
              setSelectedDate(null);
            }}
            propertyLocationId={selectedPropertyLocation?.id ?? 0}
            initialDate={selectedDate}
            onEventCreated={handleEventCreated}
          />
        )}

        {selectedEvent && (
          <CalendarEventEditModal
            event={selectedEvent}
            onClose={() => setSelectedEvent(null)}
            onEventDeleted={handleEventDeleted}
          />
        )}

        {viewingEvent && (
          <EventDetailsModal
            event={viewingEvent}
            onClose={() => setViewingEvent(null)}
            onEditClick={handleEditEvent}
            onEventUpdated={handleEventUpdated}
          />
        )}
      </Box>
    </MuiPageWrapper>
  );
};

export default CalendarView;
